import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Eye, UserRoundCog, Trash2, Building2, Loader2,
  Workflow, BarChart3, Users, CheckCircle2, ListOrdered, Bell, User,
} from 'lucide-react';
import InstitutionTrainingRemindersSlideOver, {
  type ReminderTargetKind,
} from '@/components/institution/InstitutionTrainingRemindersSlideOver';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import type { UserRole } from '@/types';
import type { ApiResponse } from '@/types/api';
import { InstitutionStyledSelect } from '@/components/institution/InstitutionStyledSelect';
import { InstitutionStyledSearchInput } from '@/components/institution/InstitutionStyledSearchInput';
import { isLyrraOne } from '@/lib/platform';

interface Flow {
  id: string;
  title: string;
  description?: string;
  status?: 'draft' | 'published' | 'archived';
  isActive?: boolean;
  participantsCount?: number;
  /** Parcours lié à une fiche catalogue (besoin entreprise). */
  fromInstitutionTrainingNeed?: boolean;
  teacher?: { id?: string; name?: string; email?: string };
  createdAt: string;
  updatedAt?: string;
}

type InstitutionUserRow = {
  id: string;
  email: string;
  name: string;
  firstName?: string | null;
  lastName?: string | null;
  roleLabel?: string;
};


/** Aligné sur FLOW_OWNER_TRANSFER_ROLES côté API (hors simple formateur). */
const REASSIGN_ROLES: UserRole[] = [
  'institution_admin',
  'institution',
  'drh',
  'rh',
  'resp_formation',
];

/** Aligné sur l’API DELETE /institution/flows/:flowId (sans resp. formation). */
const DELETE_ENTERPRISE_FLOW_ROLES: UserRole[] = [
  'institution_admin',
  'institution',
  'drh',
  'rh',
];

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  published: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  archived: 'bg-[#F0F4FA] text-[#5B6472] dark:bg-[#19335E] dark:text-[#B6CAEC]',
};

const LEARNING_PATH_STATUS_COLORS: Record<string, string> = {
  draft: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  published: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
};

export default function InstitutionFlowsPanel() {
  const { t, i18n } = useTranslation();
  const { user, hasAnyRole } = useAuth();
  const dateLocale = (i18n.resolvedLanguage ?? i18n.language ?? 'fr').replace('_', '-');
  const statusLabel = (s: string) => t(`institution.status.${s}`);
  const flowStatusFilterOptions = useMemo(
    () => [
      { value: 'all', label: t('institution.status.allStatuses') },
      { value: 'draft', label: t('institution.status.draft') },
      { value: 'published', label: t('institution.status.published') },
      { value: 'archived', label: t('institution.status.archived') },
    ],
    [t],
  );
  /** Même logique que l’API : rôles RH / resp. formation / admin institution, ou formateur avec flag admin établissement. */
  const canReassignOwner =
    !isLyrraOne() &&
    (hasAnyRole(...REASSIGN_ROLES) ||
      hasAnyRole('admin') ||
      (user?.role === 'teacher' && Boolean(user.isInstitutionAdmin)));

  const canDeleteEnterpriseFlow =
    hasAnyRole(...DELETE_ENTERPRISE_FLOW_ROLES) ||
    (user?.role === 'teacher' && Boolean(user.isInstitutionAdmin));

  const [flows, setFlows] = useState<Flow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const [transferFlow, setTransferFlow] = useState<Flow | null>(null);
  const [transferCandidates, setTransferCandidates] = useState<InstitutionUserRow[]>([]);
  const [transferUsersLoading, setTransferUsersLoading] = useState(false);
  const [selectedNewOwnerId, setSelectedNewOwnerId] = useState('');
  const [transferSubmitting, setTransferSubmitting] = useState(false);
  const [transferFeedback, setTransferFeedback] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const [deleteFlow, setDeleteFlow] = useState<Flow | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteFeedback, setDeleteFeedback] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const [reminderPanel, setReminderPanel] = useState<{
    targetKind: ReminderTargetKind;
    targetId: string;
    title: string;
  } | null>(null);

  const loadTransferUsers = useCallback(async (currentOwnerId?: string) => {
    setTransferUsersLoading(true);
    setTransferFeedback(null);
    try {
      const response = await api.get<ApiResponse<{ users: InstitutionUserRow[] }>>('/institution/users');
      const list = response.data?.users ?? [];
      const filtered = currentOwnerId ? list.filter((u) => u.id !== currentOwnerId) : list;
      setTransferCandidates(filtered);
      setSelectedNewOwnerId('');
    } catch {
      setTransferCandidates([]);
      setTransferFeedback({ type: 'err', text: t('institution.flows.transferError') });
    } finally {
      setTransferUsersLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!transferFlow) return;
    void loadTransferUsers(transferFlow.teacher?.id);
  }, [transferFlow, loadTransferUsers]);

  const closeTransferModal = () => {
    setTransferFlow(null);
    setTransferCandidates([]);
    setSelectedNewOwnerId('');
    setTransferFeedback(null);
  };

  const submitTransfer = async () => {
    if (!transferFlow || !selectedNewOwnerId) return;
    setTransferSubmitting(true);
    setTransferFeedback(null);
    try {
      const resp = await api.patch<ApiResponse<{ flow: Flow }>>(
        `/institution/flows/${transferFlow.id}/owner`,
        { newOwnerId: selectedNewOwnerId },
      );
      const updated = resp.data?.flow;
      if (updated?.teacher) {
        setFlows((prev) =>
          prev.map((f) =>
            f.id === transferFlow.id ? { ...f, teacher: { ...f.teacher, ...updated.teacher } } : f,
          ),
        );
      }
      setTransferFeedback({ type: 'ok', text: t('institution.flows.transferSuccess') });
      setTimeout(closeTransferModal, 900);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e
          ? String((e as { message: string }).message)
          : t('institution.flows.transferError');
      setTransferFeedback({ type: 'err', text: msg });
    } finally {
      setTransferSubmitting(false);
    }
  };

  const closeDeleteModal = () => {
    setDeleteFlow(null);
    setDeleteFeedback(null);
  };

  const submitDeleteEnterpriseFlow = async () => {
    if (!deleteFlow) return;
    setDeleteSubmitting(true);
    setDeleteFeedback(null);
    try {
      await api.delete(`/institution/flows/${deleteFlow.id}`);
      setFlows((prev) => prev.filter((f) => f.id !== deleteFlow.id));
      setDeleteFeedback({ type: 'ok', text: t('institution.flows.deleteEnterpriseSuccess') });
      setTimeout(closeDeleteModal, 900);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e
          ? String((e as { message: string }).message)
          : t('institution.flows.deleteEnterpriseError');
      setDeleteFeedback({ type: 'err', text: msg });
    } finally {
      setDeleteSubmitting(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const envelope = await api.get<ApiResponse<{ flows?: Flow[] }>>('/institution/flows', {
          page: 1,
          limit: 200,
        });
        if (!mounted) return;
        const list = envelope.data?.flows;
        setFlows(Array.isArray(list) ? list : []);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);


  const filtered = useMemo(() => {
    return flows.filter((f) => {
      const label = f.title ?? '';
      if (search && !label.toLowerCase().includes(search.toLowerCase())) return false;
      const effectiveStatus = f.status ?? (f.isActive ? 'published' : 'draft');
      if (statusFilter !== 'all' && effectiveStatus !== statusFilter) return false;
      return true;
    });
  }, [flows, search, statusFilter]);



  const totalFlows = flows.length;
  const publishedCount = flows.filter((f) => (f.status ?? (f.isActive ? 'published' : 'draft')) === 'published').length;

  return (
    <div className="space-y-6">
      {deleteFlow && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-enterprise-flow-title"
        >
          <div className="bg-white dark:bg-[#122644] rounded-xl border border-[#E8EDF5] dark:border-[#19335E] shadow-xl max-w-md w-full p-5 space-y-4">
            <h2 id="delete-enterprise-flow-title" className="text-lg font-semibold text-[#0C1C38] dark:text-white">
              {t('institution.flows.deleteEnterpriseTitle')}
            </h2>
            <p className="text-sm text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.flows.deleteEnterpriseHint')}</p>
            <p className="text-sm font-medium text-[#0C1C38] dark:text-[#DCE7F7] truncate" title={deleteFlow.title}>
              {deleteFlow.title}
            </p>
            {deleteFeedback && (
              <p
                className={cn(
                  'text-sm',
                  deleteFeedback.type === 'ok'
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-red-600 dark:text-red-400',
                )}
              >
                {deleteFeedback.text}
              </p>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={deleteSubmitting}
                className="px-3 py-1.5 text-sm rounded-lg border border-[#E8EDF5] dark:border-[#B6CAEC] text-[#121826] dark:text-[#DCE7F7] hover:bg-[#F8FAFC] dark:hover:bg-[#19335E] disabled:opacity-50"
              >
                {t('institution.common.cancel')}
              </button>
              <button
                type="button"
                disabled={deleteSubmitting}
                onClick={() => void submitDeleteEnterpriseFlow()}
                className="px-3 py-1.5 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleteSubmitting ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('institution.common.loading')}
                  </span>
                ) : (
                  t('institution.flows.deleteEnterpriseConfirm')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {transferFlow && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="transfer-flow-title"
        >
          <div className="bg-white dark:bg-[#122644] rounded-xl border border-[#E8EDF5] dark:border-[#19335E] shadow-xl max-w-md w-full p-5 space-y-4">
            <h2 id="transfer-flow-title" className="text-lg font-semibold text-[#0C1C38] dark:text-white">
              {t('institution.flows.transferModalTitle')}
            </h2>
            <p className="text-sm text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.flows.transferModalHint')}</p>
            <p className="text-sm font-medium text-[#0C1C38] dark:text-[#DCE7F7] truncate" title={transferFlow.title}>
              {transferFlow.title}
            </p>
            {transferUsersLoading ? (
              <p className="text-sm text-[#5B6472]">{t('institution.flows.transferLoadingUsers')}</p>
            ) : transferCandidates.length === 0 ? (
              <p className="text-sm text-amber-700 dark:text-amber-400">{t('institution.flows.transferNoCandidates')}</p>
            ) : (
              <InstitutionStyledSelect
                label={t('institution.flows.transferPickUser')}
                panelZIndexClass="z-[200]"
                options={[
                  { value: '', label: '—' },
                  ...transferCandidates.map((u) => ({
                    value: u.id,
                    label: `${u.name || `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email}${u.roleLabel ? ` (${u.roleLabel})` : ''}`,
                    icon: User,
                  })),
                ]}
                value={selectedNewOwnerId}
                onChange={setSelectedNewOwnerId}
              />
            )}
            {transferFeedback && (
              <p
                className={cn(
                  'text-sm',
                  transferFeedback.type === 'ok'
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-red-600 dark:text-red-400',
                )}
              >
                {transferFeedback.text}
              </p>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={closeTransferModal}
                className="px-3 py-1.5 text-sm rounded-lg border border-[#E8EDF5] dark:border-[#B6CAEC] text-[#121826] dark:text-[#DCE7F7] hover:bg-[#F8FAFC] dark:hover:bg-[#19335E]"
              >
                {t('institution.common.cancel')}
              </button>
              <button
                type="button"
                disabled={!selectedNewOwnerId || transferSubmitting || transferUsersLoading}
                onClick={() => void submitTransfer()}
                className="px-3 py-1.5 text-sm rounded-lg bg-primary-900 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {t('institution.flows.transferSubmit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {reminderPanel && (
        <InstitutionTrainingRemindersSlideOver
          targetKind={reminderPanel.targetKind}
          targetId={reminderPanel.targetId}
          title={reminderPanel.title}
          onClose={() => setReminderPanel(null)}
        />
      )}


      <div>
        <h1 className="text-2xl font-bold text-[#0C1C38] dark:text-white">{t('institution.flows.title')}</h1>
        <p className="text-sm text-[#5B6472] dark:text-[#B6CAEC] mt-1">{t('institution.flows.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: t('institution.flows.statTotal'), value: totalFlows, icon: Workflow, color: 'text-blue-600 dark:text-blue-400' },
          { label: t('institution.flows.statPublished'), value: publishedCount, icon: CheckCircle2, color: 'text-green-600 dark:text-green-400' },
          { label: t('institution.flows.statLearners'), value: `${flows.reduce((acc, f) => acc + (f.participantsCount ?? 0), 0)}`, icon: BarChart3, color: 'text-purple-600 dark:text-purple-400' },
        ].map((s) => (
          <div key={s.label} className="bg-white dark:bg-[#122644] rounded-xl border border-[#E8EDF5] dark:border-[#19335E] p-4 flex items-center gap-4">
            <div className={cn('p-2 rounded-lg bg-[#F8FAFC] dark:bg-[#19335E]/50', s.color)}>
              <s.icon className="h-5 w-5" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#0C1C38] dark:text-white">{s.value}</p>
              <p className="text-xs text-[#5B6472] dark:text-[#B6CAEC]">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="min-w-[200px] flex-1">
          <InstitutionStyledSearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('institution.flows.searchPh')}
          />
        </div>
        <div className="min-w-[180px] shrink-0">
          <InstitutionStyledSelect options={flowStatusFilterOptions} value={statusFilter} onChange={setStatusFilter} />
        </div>
      </div>

      <div className="bg-white dark:bg-[#122644] rounded-xl border border-[#E8EDF5] dark:border-[#19335E] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E8EDF5] dark:border-[#19335E] bg-[#F8FAFC] dark:bg-[#122644]/50">
                <th className="text-start px-4 py-3 font-medium text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.flows.colName')}</th>
                <th className="text-start px-4 py-3 font-medium text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.common.author')}</th>
                <th className="text-start px-4 py-3 font-medium text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.common.status')}</th>
                <th className="text-end px-4 py-3 font-medium text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.common.learners')}</th>
                <th className="text-end px-4 py-3 font-medium text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.common.completion')}</th>
                <th className="text-start px-4 py-3 font-medium text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.common.createdAt')}</th>
                <th className="text-end px-4 py-3 font-medium text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.common.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F0F4FA] dark:divide-[#19335E]">
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-[#5B6472]/70 dark:text-[#B6CAEC]">{t('institution.common.loading')}</td></tr>
              ) : filtered.map((flow) => (
                <tr key={flow.id} className="hover:bg-[#F8FAFC] dark:hover:bg-[#19335E]/30 transition-colors">
                  <td className="px-4 py-3 font-medium text-[#0C1C38] dark:text-white">
                    <div className="flex flex-wrap items-center gap-2">
                      <span>{flow.title}</span>
                      {flow.fromInstitutionTrainingNeed && (
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                          <Building2 className="h-3 w-3" />
                          {t('institution.flows.badgeEnterprise')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[#5B6472] dark:text-[#DCE7F7]">{flow.teacher?.name || flow.teacher?.email || t('institution.common.empty')}</td>
                  <td className="px-4 py-3">
                    <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', STATUS_COLORS[flow.status ?? (flow.isActive ? 'published' : 'draft')])}>
                      {statusLabel(flow.status ?? (flow.isActive ? 'published' : 'draft'))}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-end text-[#5B6472] dark:text-[#DCE7F7]">
                    <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" />{flow.participantsCount ?? 0}</span>
                  </td>
                  <td className="px-4 py-3 text-end text-[#5B6472] dark:text-[#DCE7F7]">{t('institution.common.empty')}</td>
                  <td className="px-4 py-3 text-[#5B6472] dark:text-[#B6CAEC]">{new Date(flow.createdAt).toLocaleDateString(dateLocale)}</td>
                  <td className="px-4 py-3 text-end">
                    <div className="inline-flex items-center justify-end gap-1">
                      <button
                        type="button"
                        title={t('institution.reminders.openPanel')}
                        onClick={() =>
                          setReminderPanel({ targetKind: 'eduflow', targetId: flow.id, title: flow.title })
                        }
                        className="rounded-lg p-1.5 text-amber-600 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-950/40"
                      >
                        <Bell className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        title={t('institution.common.view')}
                        onClick={() => window.open(`/eduflow/flows/${flow.id}/play`, '_blank')}
                        className="p-1.5 rounded-lg hover:bg-[#F0F4FA] dark:hover:bg-[#19335E] text-[#5B6472] hover:text-[#0C1C38] dark:text-[#B6CAEC] dark:hover:text-white"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      {canReassignOwner && (
                        <button
                          type="button"
                          title={t('institution.flows.transferOwner')}
                          onClick={() => setTransferFlow(flow)}
                          className="p-1.5 rounded-lg hover:bg-[#F0F4FA] dark:hover:bg-[#19335E] text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                        >
                          <UserRoundCog className="h-4 w-4" />
                        </button>
                      )}
                      {flow.fromInstitutionTrainingNeed && canDeleteEnterpriseFlow && (
                        <button
                          type="button"
                          title={t('institution.flows.deleteEnterpriseTooltip')}
                          onClick={() => setDeleteFlow(flow)}
                          className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600 dark:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-[#5B6472]/70 dark:text-[#B6CAEC]">{t('institution.flows.empty')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}
